'use client'

import {useContext, useEffect, useMemo, useState, useTransition} from 'react'
import {Button, ButtonSpinner, EnquireForm, Locale, NotifyPanel, NotifyPanelProps, Pastil, Select, SelectOptionType} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {useIsMounted, useTranslate} from '@local/hooks'
import {formatPrice} from '@local/utils/currency'
import {rangeArray} from '@local/utils/array'
import {RetreatDetailsContext} from '@local/stores/contexts'
import {useShopifyStore} from '@local/shopify'
import {ProductHeroSelectModal} from './ProductHeroSelectModal'

import EyeSVG from '@local/assets/svgs/icon-eye.svg'
import DateSVG from '@local/assets/svgs/icon-date.svg'
import BedSVG from '@local/assets/svgs/icon-bed.svg'
import PersonSVG from '@local/assets/svgs/icon-person.svg'
import ChevronSVG from '@local/assets/svgs/chevron.svg'
import PaymentSVG from '@local/assets/svgs/icon-payment.svg'
import EnvelopeSVG from '@local/assets/svgs/envelope-regular.svg'

import {SellingPlan} from '@local/types'
import {post} from '@local/utils/fetch'
import {usePathname} from 'next/navigation'

export interface ProductHeroMenuProps {
	className?: string
	variant: 'default' | 'coming-soon',
	notifyPanel: NotifyPanelProps['notifyPanel']
	sellingPlans: SellingPlan[]
	country?: string,
	enquireForm: {
		listID: string
		description: string
		successText: string
		successTitle: string
		retreatList: Array<{ label: string, id: string, value: string }>
	}
}

type GuestAndRoomType = {
	guests: SelectOptionType
	date: SelectOptionType
	room: SelectOptionType
	sellingPlan: { value: string, label?: string }
}

export const ProductHeroMenu = ({className, enquireForm, variant, sellingPlans, notifyPanel, country}: ProductHeroMenuProps) => {
	let {setIsOpen: setIsInfoOpen, product: productData, variants} = useContext(RetreatDetailsContext)
	variants = variants.filter(Boolean)
	const locale = useTranslate()
	const createCart = useShopifyStore(state => state.createCart)
	const [isHelpModalOpen, setIsHelpModalOpen] = useState<boolean>(false)
	const isMounted = useIsMounted()
	const pathname = usePathname()
	// Get Room Dropdown Options
	let roomOptions: SelectOptionType[] = []
	let dateOptions: SelectOptionType[] = []
	productData.store.options.forEach((option) => {
		if (option.name === 'Room') {
			roomOptions = option.values.map((value) => ({value, label: value}))
		}
		if (option.name === 'Date') {
			dateOptions = option.values.map((value) => ({value, label: value}))
		}
	})

	// Get Guest Dopdown Options
	const allGuestOptions = useMemo(() => rangeArray(1, productData?.maxDropdownCount || productData?.maxPeopleCount || 1).map((value) => ({
		value,
		label: `${value} ${value === 1 ? locale('Guest') : locale('Guests')}`,
	}),
	), [productData?.maxDropdownCount, productData?.maxPeopleCount])

	const [guestOptions, setGuestOptions] = useState<SelectOptionType[]>(allGuestOptions)

	const [isRoomOpen, setIsRoomOpen] = useState<boolean>(false)
	const onClickCloseRoom = () => { setIsRoomOpen(false) }
	const [isComingSoonOpen, setIsComingSoonOpen] = useState<boolean>(false)
	const onClickCloseComingSoon = () => { setIsComingSoonOpen(false) }

	const [price, setPrice] = useState<number>(0)
	const [currencyCode, setCurrencyCode] = useState('EUR')
	const [guestAndRoom, setGuestAndRoom] = useState<GuestAndRoomType>({
		guests: {value: '', label: ''},
		date: {value: '', label: ''},
		room: {value: '', label: ''},
		sellingPlan: {value: 'full', label: ''},
	})
	const [isAvailable, setIsAvailable] = useState<boolean>(true)

	const sellingPlanOptions = sellingPlans?.length
		? [
			{value: 'full', label: 'Full payment'},
			...sellingPlans.map(sellingPlan => ({value: sellingPlan.id, label: sellingPlan.name})),
		]
		: []

	const [checkoutUrl, setCheckoutUrl] = useState<string | undefined>(undefined)
	const [showTooltip, setShowTooltip] = useState<boolean>(false)
	const [isLoading, startTransition] = useTransition()

	const handleGuests = (e) => {
		const value = e.target.value
		const _guest = guestOptions.find((option) => parseInt(option.value) === parseInt(value))
		const _roomPrice: number | { amount: string } = variants.find(room => room.store.option1 === guestAndRoom.room.value)?.store.price || 0

		// update price
		if (_guest?.value && _roomPrice) {
			const _guests = parseInt(_guest.value)
			const _roomPriceInt = typeof _roomPrice === 'number' ? _roomPrice : parseInt(_roomPrice.amount)
			setPrice(_guests * _roomPriceInt)
		}

		// update state
		if (_guest?.label) {
			setGuestAndRoom(prev => ({...prev, guests: {value, label: _guest.label}}))
		}
	}

	const handleRoom = (e) => {
		const value = e.target.value
		setRoom(value, guestAndRoom.date.value)
	}

	const handleDate = (e) => {
		const date = e.target.value
		if (guestAndRoom.room.value) {
			setRoom(guestAndRoom.room.value, date)
		} else {
			setGuestAndRoom(prev => ({...prev, date: {value: date, label: date}}))
		}
	}

	const handleSellingPlan = (e) => {
		const value = e.target.value
		setGuestAndRoom(prev => ({...prev, sellingPlan: {value}}))
	}

	const setRoom = (value, date) => {
		// check for room variant
		const _room = variants.find((variant) => {
			if (dateOptions.length > 1 && date) {
				return variant.store.option1 === value && variant.store.option2 === date
			} else {
				return variant.store.option1 === value
			}
		})
		const _roomPrice: number = (typeof _room?.store.price === 'number' ? _room?.store.price : parseInt(_room.store.price.amount)) || 0
		setCurrencyCode(_room.store.price.currencyCode)
		let _guests = parseInt(guestAndRoom.guests.value)

		// update state
		if (_room?.title) {
			const newGuestOptions = allGuestOptions.filter(option => (option.value % (_room?.peoplePerRoom || 1)) === 0)
			setGuestOptions(newGuestOptions)

			if (!newGuestOptions.find(option => option.value === guestAndRoom.guests.value)) {
				const _nearestGuestOption = newGuestOptions[0]
				setGuestAndRoom(prev => ({
					...prev,
					room: {value, label: value},
					guests: _nearestGuestOption,
					date: {value: date, label: date},
				}))
				_guests = parseInt(_nearestGuestOption.value)
			} else {
				setGuestAndRoom(prev => ({
					...prev,
					room: {value, label: value},
					date: {value: date, label: date},
				}))
			}
		}

		// update price
		if (_guests && _roomPrice) {
			setPrice(_guests * _roomPrice)
		}
	}

	const getCart = async (room: string, date: string, guests: string) => {
		const variant = variants.find(variant => (
			variant.store.option1 === room && (dateOptions.length > 1 ? variant.store.option2 === date : true)
		))

		if (variant && variant?.store.quantityAvailable < parseInt(guests)) {
			setIsAvailable(false)
			setCheckoutUrl(undefined)
			return
		}

		if (variant?.store.gid && variant?.store.isAvailable) {
			setIsAvailable(true)

			const cart = await createCart([{
				merchandiseId: variant?.store.gid,
				quantity: parseInt(guestAndRoom.guests.value),
				sellingPlanId: guestAndRoom.sellingPlan.value === 'full' ? undefined : guestAndRoom.sellingPlan.value,
				attributes: [{
					key: 'Booking for',
					value: `${guestAndRoom.guests.value} ${parseInt(guestAndRoom.guests.value) > 1 ? locale('Guests') : locale('Guest')}`,
				}],

			}], country)

			setCheckoutUrl(cart.checkoutUrl)
		} else {
			setIsAvailable(false)
			setCheckoutUrl(undefined)
		}
	}

	useEffect(() => {
		if (
			guestAndRoom.guests.value &&
			guestAndRoom.room.value &&
			(guestAndRoom.date.value || dateOptions.length <= 1)
		) {
			startTransition(() => getCart(guestAndRoom.room.value, guestAndRoom.date.value, guestAndRoom.guests.value))
		}
	}, [guestAndRoom])

	useEffect(() => {
		if (isMounted) {
			post('/api/fb-conversion', {event: 'ViewContent', pathname, data: {id: productData._id, title: productData.title, category: 'experience', type: 'product'}})
		}
	}, [isMounted])

	const priceDisplayed = price !== 0 && <span> — {formatPrice(guestAndRoom.sellingPlan.value === 'full' ? price : price / 5, `en-${country}`, currencyCode, {minimumFractionDigits: 0, maximumFractionDigits: 0}).replace(/\s/g, '')}</span>
	// todo: move this logic to sanity schema
	const hideMenu = pathname.includes('/transformational-incentive-travel') || pathname.includes('/custom-experience') || pathname.includes('/corporate-experience') || pathname.includes('/the-conscious-leader') || pathname.includes('/the-high-performance-team') || pathname.includes('the-executive-reset') || pathname.includes('/kaer-for-business') || pathname.includes('/transformational-off-sites')
	if (hideMenu) {
		return null
	}
	return (
		<>
			<div className={cx(
				'fixed z-3 bottom-14 lg:bottom-20 margin-right-1 lg-max:margin-left-1',
				className,
			)}>
				<div className={cx(
					'relative flex w-full lg:justify-end gap-8 z-1',
					variant === 'coming-soon' && 'lg:span-w-3-wider',
				)}>
					<Button
						variant='secondary-icon'
						onClick={() => setIsInfoOpen(true)}
						label='Open Information'
						className='flex-shrink-0 hidden lg:flex w-44 h-44 shadow-[0px_0px_3.6px_rgba(0,0,0,0.024),0px_0px_10px_rgba(0,0,0,0.035),0px_0px_24.1px_rgba(0,0,0,0.046),0px_0px_80px_rgba(0,0,0,0.07)]'>
						<EyeSVG className='w-18 h-18' alt='Info Icon' />
					</Button>
					<Button
						variant='secondary'
						small
						className='flex-shrink-0 hidden lg:flex  fill-green hover:fill-white'
						icon={<EnvelopeSVG className='lg-max:!-mr-10 lg:hidden ' />}
						onClick={() => setIsHelpModalOpen(true)}>
						<span className='lg-max:hidden text-11 lg:mr-10 uppercase'>Contact</span>
					</Button>
					<div className={cx(
						'flex items-center text-black lg-max:w-full',
						'shadow-[0px_0px_3.6px_rgba(0,0,0,0.024),0px_0px_10px_rgba(0,0,0,0.035),0px_0px_24.1px_rgba(0,0,0,0.046),0px_0px_80px_rgba(0,0,0,0.07)]',
						variant === 'coming-soon' && 'hidden',
					)}>
						<Button
							small
							variant='secondary'
							label='Select room and guests'
							onClick={() => { setIsRoomOpen(!isRoomOpen) }}
							className={cx(
								'lg:hidden w-full',
								'rounded-e-none pl-16 pr-12 justify-between',
								guestAndRoom.guests?.label && guestAndRoom.room.label && 'px-10',
							)}>
							{guestAndRoom.guests.label && guestAndRoom.room.label
								? (
									<span className='text-11 font-medium tracking-wide overflow-clip overflow-ellipsis span-w-4-wide whitespace-nowrap'>
										{guestAndRoom.guests.label}, {guestAndRoom.room.label}
									</span>
								)
								: (
									<>
										<span className='inline-flex items-center gap-6 span-w-4-wide'>
											<BedSVG className='w-16 h-16' alt='Bed Icon' />
											<span>{locale('Room Type')}</span>
										</span>
									</>
								)}
							<ChevronSVG
								className={cx(
									'w-8 h-8 transfrom-gpu duration-300 ease-quart-out transition-transform',
									isRoomOpen ? 'rotate-180' : 'rotate-0 duration-200',
								)}
								aria-hidden />
						</Button>

						<Button onClick={() => setIsInfoOpen(true)} variant='secondary' small className='lg:hidden flex-shrink-0 rounded-none px-8 text-black border-0 border-l-2 border-grey-1'>
							<span className='text-12 font-medium text-black whitespace-nowrap'>Details</span>
						</Button>

						{/* desktop */}
						<div className='flex lg-max:hidden'>
							{dateOptions.length > 1 && (
								<Select
									key={`date-select-${guestAndRoom.date.value}`}
									theme='light'
									name='date'
									id='product-hero-date-select'
									placeholder={locale('Date')}
									onChange={handleDate}
									options={dateOptions}
									value={guestAndRoom.date.value}
									containerClassName='border-0 span-w-2'
									className='border-0 border-r rounded-e-none capitalize'
									titleClassName='text-11 tracking-wide font-normal overflow-clip overflow-ellipsis whitespace-nowrap uppercase'
									prefixIcon={!guestAndRoom.date.value && <DateSVG className='w-16 h-16 mr-4' />} />
							)}
							<Select
								key={`room-select-${guestAndRoom.room.value}`}
								theme='light'
								name='room'
								id='product-hero-room-select'
								placeholder={locale('Room Type')}
								onChange={handleRoom}
								options={roomOptions}
								value={guestAndRoom.room.value}
								containerClassName='border-0 w-full lg:w-180'
								className={cx(
									'border-0 border-r capitalize',
									dateOptions.length > 1 ? '!rounded-none' : 'rounded-e-none',
								)}
								keepPrefixIcon
								titleClassName='text-11 tracking-wide font-normal overflow-clip overflow-ellipsis whitespace-nowrap uppercase'
								prefixIcon={<BedSVG className='w-16 h-16 mr-4 shrink-0' />} />
							<Select
								key={`guest-select-${guestAndRoom.guests.value}`}
								theme='light'
								name='guest'
								id='product-hero-guest-select'
								placeholder={locale('Guests Total')}
								onChange={handleGuests}
								options={guestOptions}
								value={guestAndRoom.guests.value}
								containerClassName='border-0 w-180'
								className='border-0 border-r !rounded-none'
								keepPrefixIcon
								prefixIcon={<PersonSVG className='w-16 h-16 mr-4 shrink-0' />} />
							{!!sellingPlanOptions.length && (
								<Select
									theme='light'
									name='sellingPlan'
									id='product-hero-selling-plan'
									placeholder={locale('Deffered Payment')}
									onChange={handleSellingPlan}
									options={sellingPlanOptions}
									value={guestAndRoom.sellingPlan.value}
									containerClassName='border-0 w-180'
									className='border-0 border-r !rounded-none'
									keepPrefixIcon
									prefixIcon={<PaymentSVG className='w-16 h-16 mr-4 shrink-0' />}
									titleClassName='text-11 tracking-wide font-normal overflow-clip overflow-ellipsis whitespace-nowrap uppercase' />
							)}
						</div>

						<div
							className='relative w-full'
							onMouseEnter={() => { if (!guestAndRoom.room.value || !guestAndRoom.guests.value || (dateOptions.length > 1 && !guestAndRoom.date.value)) setShowTooltip(true) }}
							onMouseLeave={() => { if (!guestAndRoom.room.value || !guestAndRoom.guests.value || (dateOptions.length > 1 && !guestAndRoom.date.value)) setShowTooltip(false) }}>
							<ButtonSpinner
								small
								variant='primary'
								href={checkoutUrl}
								target='_self'
								loading={isLoading}
								className={cx(
									'rounded-s-none w-full lg:w-180 px-10',
									!checkoutUrl && 'pointer-events-none',
									isLoading && '!bg-black !border-black',
								)}>
								{isAvailable
									? (
										<span>
											<span>{locale('Book')}</span>
											{priceDisplayed}
										</span>
									)
									: (
										<Locale>Fully Booked</Locale>
									)}
							</ButtonSpinner>
							<Pastil
								variant='filled'
								className={cx(
									'absolute bottom-full left-1/2 -translate-x-1/2 mb-10',
									'transition-[opacity,transform,visibility] duration-200 ease-quart-out origin-bottom whitespace-nowrap',
									showTooltip ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible',
								)}>
								{dateOptions.length > 1 ? locale('Select Date, Room and Guest') : locale('Select Room and Guest')}
							</Pastil>
						</div>

					</div>

					<Button
						small
						variant='primary'
						onClick={() => { setIsComingSoonOpen(true) }}
						className={cx(
							'flex-1',
							variant !== 'coming-soon' && 'hidden',
						)}>
						{locale('Notify Me (Coming Soon)')}
					</Button>
				</div>
			</div>

			{/* mobile only */}
			<ProductHeroSelectModal
				key={`${guestAndRoom.room.value}-${guestAndRoom.guests.value}`}
				isOpen={isRoomOpen}
				onClickClose={onClickCloseRoom}
				guestOptions={guestOptions}
				roomOptions={roomOptions}
				dateOptions={dateOptions}
				sellingPlanOptions={sellingPlanOptions}
				guestAndRoom={guestAndRoom}
				onChangeGuests={handleGuests}
				onChangeRoom={handleRoom}
				onChangeSellingPlan={handleSellingPlan}
				onChangeDate={handleDate} />

			{variant === 'coming-soon' && (
				<NotifyPanel
					isOpen={isComingSoonOpen}
					onClickClose={onClickCloseComingSoon}
					retreat={productData.title}
					notifyPanel={notifyPanel} />
			)}
			{enquireForm && (
				<EnquireForm {...enquireForm} isOpen={isHelpModalOpen} onClickClose={() => setIsHelpModalOpen(false)} />
			)}
		</>
	)
}
